import React, { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  IFilterJob,
  IJobCities,
  IJobFilters,
  JobCitiesResultsType
} from 'core/interface/job';
import useLocales from '../../../hooks/useLocales';
import { JobService } from 'services/jobs/job';
import { getCareerLevelLabels, getSalaryLabels } from 'constants/constants';
import Checkboxes from 'components/shared/Checkbox/Index';
import Autocomplete from 'components/shared/Autocomplete/Index';
import Button from 'components/Button/Index';
import { ProfileService } from 'services/profile/profile';
import DropDown from 'components/Dropdown';
import {
  degreeTypes,
  companySizeChoices
} from 'core/constants/aboutMePopUpConstants';
import { RootState } from 'redux/store';
import { IndustryRedux } from 'core/interface/education';
import { SkillService } from 'services/skills/skillService';
import { SearchInitialValue } from 'core/interface/searchInitialValue';
import { IAutocompleteItem } from 'core/interface/autocompleteItem';
import { Form, Formik } from 'formik';
import Multiselect from 'components/shared/Multiselect/Index';
import Image from 'components/image/Image';
import { contractTypes } from 'core/constants/workType';
import { hourlyRateChoices } from 'core/constants/preferenceConstats';
import styles from './index.module.scss';
import { LanguageService } from 'services/language';
import { ICandidateLanguage } from 'core/interface/candidate-language';
import { useDebounce } from 'helpers/useDebounce';
import { IIndustry } from 'core/interface/companies';

const searchInitialValue: SearchInitialValue<
  IIndustry[] | IAutocompleteItem[] | IJobCities | null
> = {
  industry: {
    value: '',
    interval: null,
    func: (query: string) => ProfileService.getIndustries(query)
  },
  computer_skill: {
    value: '',
    interval: null,
    func: (query: string) => SkillService.getITSkillsList(query)
  },
  location: {
    value: '',
    interval: null,
    func: (query: string) =>
      JobService.getLocations({
        q: query
      })
  }
};
const JobFilters: FC<IFilterJob> = ({ applyFilter }): JSX.Element => {
  const { t } = useLocales();
  const industries = useSelector(
    (selector: IndustryRedux) => selector.industries?.industry || []
  );
  const { computerSkills: skills } = useSelector(
    (selector: RootState) => selector.computerSkills || []
  );

  const { languages } = useSelector((state: RootState) => state.languages);

  const [resetDropdown, setResetDropdown] = useState<boolean>(false);
  const [locationOptions, setLocationOptions] = useState<
    JobCitiesResultsType[]
  >([]);
  const [locationQuery, setLocationQuery] = useState('');
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const debouncedLocationQuery = useDebounce(locationQuery, 500);
  const skipApplyOnMount = useRef(true);

  const [values, setValues] = useState<IJobFilters>({
    industry: '',
    degree_type: '',
    computer_skill: [],
    location: {
      id: 0,
      name: ''
    },
    compensations: [],
    career_level: [],
    languages: [] as ICandidateLanguage[],
    is_remote: false,
    contracts: [],
    company_sizes: []
  });

  const [search, setSearch] =
    useState<
      SearchInitialValue<
        IIndustry[] | IAutocompleteItem[] | IJobCities | IJobCities | null
      >
    >(searchInitialValue);

  const handleCheckbox = (e: string, key: string): void => {
    const current = [...(values[key] as string[])];
    const index = current.indexOf(e);
    if (index !== -1) {
      current.splice(index, 1);
    } else {
      current.push(e);
    }

    setValues({
      ...values,
      [key]: current
    });
  };

  const onInputChange = (e: string, type: string): void => {
    const searchNewData = { ...searchInitialValue };
    searchNewData[type].value = e;
    if (searchNewData[type].interval) {
      clearTimeout(searchNewData[type].interval as NodeJS.Timeout);
    }
    searchNewData[type].interval = setTimeout(() => {
      const query = searchNewData[type].value?.trim();
      if (query) {
        searchNewData[type].func(query);
      }
    }, 500);
    setSearch(searchNewData);
  };

  useEffect(() => {
    if (skipApplyOnMount.current) {
      skipApplyOnMount.current = false;
      return;
    }
    applyFilter(values);
  }, [values]);

  useEffect(() => {
    if (!debouncedLocationQuery.trim()) {
      setLocationOptions([]);
      return;
    }

    let cancelled = false;
    setIsLoadingLocations(true);
    JobService.getLocations({ q: debouncedLocationQuery })
      .then((res) => {
        if (!cancelled) {
          setLocationOptions(res?.results || []);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingLocations(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedLocationQuery]);

  const handleLoadMoreCities = () => {
    if (isLoadingLocations || !debouncedLocationQuery.trim()) {
      return;
    }
    setIsLoadingLocations(true);
    JobService.getLocations(
      {
        limit: 10,
        offset: locationOptions.length,
        q: debouncedLocationQuery
      },
      true
    )
      .then((res) => {
        if (res?.results?.length) {
          setLocationOptions((prev) => {
            const existingIds = new Set(prev.map((city) => city.id));
            const next = res.results.filter(
              (city: JobCitiesResultsType) => !existingIds.has(city.id)
            );
            return [...prev, ...next];
          });
        }
      })
      .finally(() => setIsLoadingLocations(false));
  };

  const handleResetFilters = () => {
    const val = {
      industry: '',
      degree_type: '',
      computer_skill: [],
      location: {
        id: 0,
        name: ''
      },
      compensations: [],
      career_level: [],
      languages: [] as ICandidateLanguage[],
      is_remote: false,
      contracts: [],
      company_sizes: []
    };

    setValues(val);
    setLocationQuery('');
    setLocationOptions([]);
    setResetDropdown(true);
    setSearch({
      industry: {
        value: '',
        interval: null,
        func: (query: string) => ProfileService.getIndustries(query)
      },
      computer_skill: {
        value: '',
        interval: null,
        func: (query: string) => SkillService.getITSkillsList(query)
      },
      location: {
        value: '',
        interval: null,
        func: (query: string) =>
          JobService.getLocations({
            q: query
          })
      }
    });

    setResetDropdown(false);
    setTimeout(() => {
      setResetDropdown(true);
    }, 0);
  };

  const handleComputerSkillChange = (
    computerSkill: string | IAutocompleteItem[]
  ): void => {
    if (typeof computerSkill != 'string' && computerSkill) {
      const newSkills = computerSkill
        .map((skill) => {
          if (typeof skill === 'object' && 'id' in skill) {
            return String(skill.id);
          }
          const foundSkill = skills?.find((s) => s.name === skill);
          return String(foundSkill?.id || '');
        })
        .filter((id) => id !== '' && !values.computer_skill.includes(id));

      setValues((prev) => ({
        ...prev,
        computer_skill: [...prev.computer_skill, ...newSkills]
      }));
    }
  };

  const getLanguages = (q?: string) => {
    if (!q?.trim()) {
      return;
    }
    LanguageService.getAllLanguages(q);
  };

  const handleRemoveLanguage = (item: ICandidateLanguage): void => {
    setValues({
      ...values,
      languages: values?.languages.filter(
        (e: ICandidateLanguage) => e.id !== item.id
      )
    });
  };

  const handleClearIndustry = () => {
    setValues({
      ...values,
      industry: ''
    });
  };

  const handleClearDegreeType = () => {
    setValues({
      ...values,
      degree_type: ''
    });
  };

  const handleClearLocation = () => {
    setValues({
      ...values,
      location: {
        id: 0,
        name: ''
      }
    });
    setLocationQuery('');
    setLocationOptions([]);
  };

  const renderCompensationSection = () => {
    return (
      <div className={styles.filters_checkbox}>
        <h1>{t('Compensations (EUR)')}</h1>

        <h3 className={styles.section_header}>{t('Monthly Salary')}</h3>
        {getSalaryLabels(values.compensations).map((e, i) => (
          <Checkboxes
            checkboxes={e}
            onArrayChange={(e) => {
              if (Array.isArray(e)) {
                handleCheckbox(e[0].value ?? e[0].label, 'compensations');
              }
            }}
            key={`monthly-${i}`}
          />
        ))}

        <h3 className={styles.section_header}>{t('Hourly Rate')}</h3>
        {hourlyRateChoices.map((choice, i) => (
          <Checkboxes
            checkboxes={[
              {
                label: String(choice.to_show),
                isChecked: values.compensations?.includes(String(choice.value)),
                value: String(choice.value)
              }
            ]}
            onArrayChange={(e) => {
              if (Array.isArray(e)) {
                handleCheckbox(e[0].value ?? e[0].label, 'compensations');
              }
            }}
            key={`hourly-${i}`}
          />
        ))}
      </div>
    );
  };

  return (
    <Formik
      className={styles.filters}
      initialValues={{}}
      onSubmit={(e) => console.log(e)}
    >
      <Form className={styles.filters}>
        <div className={styles.filters}>
          <h1>{t('Filter')}</h1>
          <div className={styles.filters_checkbox}>
            <h1 className="label">{t('Industry')}</h1>
            <Autocomplete
              fieldName="industry"
              show_arrow={false}
              value={
                industries.find(
                  (industry) => String(industry?.id) === String(values.industry)
                )?.name ?? ''
              }
              options={
                industries?.map((e) => ({
                  id: e?.id,
                  title: e?.name
                })) || []
              }
              show_location={false}
              placeholder={t('Type to search…')}
              onChange={(e) => {
                const industryId = industries.find(
                  (industry) => industry.name === e
                )?.id;
                if (!industryId) {
                  return;
                }
                setValues((prev) => ({
                  ...prev,
                  industry: String(industryId)
                }));
              }}
              onInputChange={(e) => onInputChange(e, 'industry')}
              values={industries}
              onClear={handleClearIndustry}
            />
          </div>
          <div className={styles.filters_checkbox}>
            <h1 className="label">{t('Degree Type')}</h1>
            <DropDown
              onReset={resetDropdown}
              defaultValue={values.degree_type}
              placeholder={t('Degree type')}
              dropdownClassName={`dashboard__left-dropdown ${styles.filters_dropdown}`}
              items={degreeTypes}
              selectedValue={(e) =>
                setValues((prev) => ({ ...prev, degree_type: e }))
              }
              onClear={handleClearDegreeType}
            />
          </div>

          <div className={styles.filters_checkbox}>
            <h1 className="label">{t('Skills')}</h1>
            <Multiselect
              fieldName="computer_skill"
              value={values.computer_skill
                .map((id) => {
                  const skill = skills?.find((s) => String(s.id) === id);
                  return skill
                    ? { id: skill.id, title: skill.name, name: skill.name }
                    : null;
                })
                .filter(Boolean)}
              options={
                skills?.map((e: IAutocompleteItem) => ({
                  id: e?.id,
                  title: e?.name,
                  name: e?.name
                })) || []
              }
              show_location={false}
              show_arrow={false}
              placeholder={t('Type to search…')}
              onChange={(e) => {
                if (typeof e !== 'string' && Array.isArray(e)) {
                  handleComputerSkillChange(e);
                }
              }}
              onInputChange={(e) => onInputChange(e || '', 'computer_skill')}
            />
            <div className={styles.selected_skills}>
              {values.computer_skill.map((id) => {
                const skill = skills?.find((s) => String(s.id) === id);
                return skill ? (
                  <span key={id} className={styles.selected_skill}>
                    {skill.name}
                    <span
                      onClick={() => {
                        setValues({
                          ...values,
                          computer_skill: values.computer_skill.filter(
                            (skillId) => skillId !== id
                          )
                        });
                      }}
                    >
                      <Image
                        className={styles.remove}
                        name="removes.svg"
                        width={10}
                        height={10}
                      />
                    </span>
                  </span>
                ) : null;
              })}
            </div>
          </div>

          {/* incase we later want a freesolo filter */}
          {/* here goes skills filter*/}
          {/* <div className={styles.filters_checkbox}>
            <h1 className="label">{t('Skills')}</h1>
            <Multiselect
              options={
                skills?.map((e) => ({
                  id: e?.id,
                  title: e?.name,
                  name: e?.name,
                  isComputerSkill: true
                })) || []
              }
              show_location={false}
              placeholder={t('Enter skills')}
              freeSolo={true}
              onChange={(value) => {
                if (!Array.isArray(value)) return;

                const newSkills = value
                  .map((item) => {
                    // Case 1: Direct string (from freeSolo/handleKeyPress)
                    if (typeof item === 'string') {
                      // Check if it matches a computer skill
                      const matchedSkill = skills?.find(
                        (s) => s.name.toLowerCase() === item.toLowerCase()
                      );
                      if (matchedSkill) {
                        return {
                          id: matchedSkill.id,
                          title: matchedSkill.name,
                          name: matchedSkill.name,
                          isComputerSkill: true
                        };
                      }
                      // Free-form skill
                      return {
                        id: item,
                        title: item,
                        name: item,
                        isComputerSkill: false
                      };
                    }

                    // Case 2: Object (from dropdown or existing selection)
                    if (item && typeof item === 'object') {
                      // If it's already in the correct format, return as is
                      if ('isComputerSkill' in item) {
                        return item;
                      }
                      // If it's from dropdown but missing isComputerSkill
                      if ('id' in item) {
                        return {
                          ...item,
                          isComputerSkill: true
                        };
                      }
                    }

                    // Fallback case (shouldn't happen)
                    return null;
                  })
                  .filter(Boolean);

                // Remove duplicates based on id for computer skills and title for free-form
                const uniqueSkills = newSkills.reduce((acc: any[], skill) => {
                  const isDuplicate = acc.some((existing) =>
                    skill.isComputerSkill
                      ? String(existing.id) === String(skill.id)
                      : existing.title === skill.title
                  );
                  if (!isDuplicate) {
                    acc.push(skill);
                  }
                  return acc;
                }, []);

                setValues((prev) => ({
                  ...prev,
                  skills: uniqueSkills
                }));
              }}
              onInputChange={(e) => onInputChange(e || '', 'skills')}
              value={values.skills}
              fieldName="skills"
            />

            <div className={styles.languages}>
              {values.skills?.map((skill: any, index: number) => (
                <span
                  key={skill.isComputerSkill ? skill.id : `free-${index}`}
                  className={styles.language}
                >
                  {skill.name || skill.title}
                  <span
                    onClick={() => {
                      setValues((prev) => ({
                        ...prev,
                        skills: prev.skills.filter((s: any) =>
                          skill.isComputerSkill
                            ? String(s.id) !== String(skill.id)
                            : s.title !== skill.title
                        )
                      }));
                    }}
                  >
                    <Image
                      className={styles.remove}
                      name="removes.svg"
                      width={10}
                      height={10}
                    />
                  </span>
                </span>
              ))}
            </div>
          </div> */}
          <div className={styles.filters_checkbox}>
            <h1 className="label">{t('City')}</h1>
            <Autocomplete
              fieldName="location"
              show_arrow={false}
              value={values.location.name}
              options={
                locationOptions.map((e) => ({
                  id: e?.id,
                  title: e?.display_name
                })) || []
              }
              show_location={true}
              placeholder={t('Type to search…')}
              onChange={(location) => {
                const activeItem = locationOptions.find(
                  (city) => city.display_name === location
                );
                if (!activeItem?.id) {
                  return;
                }

                setValues({
                  ...values,
                  location: {
                    id: activeItem.id,
                    name: activeItem.display_name
                  }
                });
              }}
              onInputChange={(e) => setLocationQuery(e)}
              values={locationOptions}
              loadMore={handleLoadMoreCities}
              onClear={handleClearLocation}
            />
          </div>
          <div className={styles.filters_checkbox}>
            <h1>{t('Contract Type')}</h1>
            {contractTypes.map((contract, i) => (
              <Checkboxes
                key={i}
                checkboxes={[
                  {
                    label: String(contract.to_show),
                    value: String(contract.value),
                    isChecked: values.contracts.includes(String(contract.value))
                  }
                ]}
                onArrayChange={(e) => {
                  if (Array.isArray(e) && e[0] && (e[0].value || e[0].label)) {
                    const value = e[0].value ? String(e[0].value) : e[0].label;
                    handleCheckbox(value, 'contracts');
                  }
                }}
              />
            ))}
          </div>
          {renderCompensationSection()}
          <div className={styles.filters_checkbox}>
            <h1>{t('Company Size')}</h1>
            {companySizeChoices.map((companySize, i) => (
              <Checkboxes
                key={i}
                checkboxes={[
                  {
                    label: String(companySize.to_show),
                    value: String(companySize.value),
                    isChecked:
                      Array.isArray(values.company_sizes) &&
                      values.company_sizes.includes(String(companySize.value))
                  }
                ]}
                onArrayChange={(e) => {
                  if (Array.isArray(e) && e[0] && (e[0].value || e[0].label)) {
                    const value = e[0].value ? String(e[0].value) : e[0].label;
                    handleCheckbox(value, 'company_sizes');
                  }
                }}
              />
            ))}
          </div>
          <div className={styles.filters_checkbox}>
            <h1>{t('Career level')}</h1>
            {getCareerLevelLabels(values.career_level).map((e, i) => (
              <Checkboxes
                checkboxes={e}
                onArrayChange={(e) => {
                  if (Array.isArray(e)) {
                    handleCheckbox(e[0].value ?? e[0].label, 'career_level');
                  }
                }}
                key={i}
              />
            ))}
          </div>
          <div
            className={styles.filters_checkbox}
            style={{ marginTop: '25px' }}
          >
            <Multiselect
              options={
                languages
                  ?.map((e) => ({
                    id: e?.id,
                    title: e?.name
                  }))
                  .filter(
                    (language) =>
                      // Filter out languages that are already selected
                      !values.languages.some(
                        (selectedLang) => selectedLang.id === language.id
                      )
                  ) || []
              }
              show_location={false}
              show_arrow={false}
              placeholder={t('Type to search…')}
              onInputChange={(e) => {
                if (e?.trim()) {
                  getLanguages(e);
                }
              }}
              onChange={(e: any) => {
                if (typeof e != 'string') {
                  // Filter out duplicates based on language id
                  const uniqueLanguages = e.reduce(
                    (acc: ICandidateLanguage[], lang: ICandidateLanguage) => {
                      const isDuplicate = acc.some(
                        (item) => item.id === lang.id
                      );
                      if (!isDuplicate) {
                        acc.push(lang);
                      }
                      return acc;
                    },
                    []
                  );

                  setValues({
                    ...values,
                    languages: uniqueLanguages
                  });
                }
              }}
              value={values?.languages}
              fieldName="language"
            />

            <div className={styles.languages}>
              {values.languages.map((language: any) => {
                return (
                  <span key={language.id} className={styles.language}>
                    {language.name || language.title}
                    <span onClick={() => handleRemoveLanguage(language)}>
                      <Image
                        className={styles.remove}
                        name="removes.svg"
                        width={10}
                        height={10}
                      />
                    </span>
                  </span>
                );
              })}
            </div>
          </div>

          <div className="field ">
            <Checkboxes
              onArrayChange={(e) => {
                if (Array.isArray(e)) {
                  setValues((prev) => ({
                    ...prev,
                    is_remote: e[0].isChecked || false
                  }));
                }
              }}
              checkboxes={[
                {
                  label: t('Remote'),
                  isChecked: values?.is_remote
                }
              ]}
            />
          </div>
          <div className={styles.filters_reset_button}>
            <Button
              t={t}
              className={`navbar__left-btn ${styles.filters_reset} ${styles.filters_buttons}`}
              title={t('Reset Filters')}
              onClick={handleResetFilters}
            />
          </div>
        </div>
      </Form>
    </Formik>
  );
};

export default JobFilters;
